/**
 * The people a family has authorized to collect one child, with a face for each.
 *
 * Student headshots answered "is this the right child?". This is the other half of the
 * handover: a teacher releasing a child to a grandparent they have never met has a name on a
 * list and, until now, nothing to check it against. What is uploaded here is what appears on
 * the check-out sheet beside that name.
 *
 * Admin-managed in v1. Families add their own contacts in the parent portal, but the photos
 * arrive centrally at info@ the same way student headshots do, and an admin asked to "add
 * grandma" needs to be able to do the whole job in one place — hence add and remove live here
 * too rather than the photo control alone.
 *
 * Every write here is immediate, not staged behind a Save. A photo upload has no sensible
 * "cancel" once the bytes are in the bucket, and a half-saved authorization list is a worse
 * failure than an extra confirm.
 */

import { useRef, useState } from 'react';
import { Camera, Loader2, Plus, Trash2, X } from 'lucide-react';
import {
  useAddAuthorizedPickup,
  useAuthorizedPickups,
  useRemoveAuthorizedPickup,
  useSetAuthorizedPickupPhoto,
} from '../../hooks/useAttendance';
import { useStudentPhotoUrls } from '../../hooks/useStudentPhotoUrls';
import { StudentAvatar } from '../../components/StudentAvatar';
import { StudentPhotoLightbox } from '../../components/StudentPhotoLightbox';
import { Modal } from '../../components/Modal';
import type { AuthorizedPickup } from '../../types/attendance';

const ACCEPTED = 'image/jpeg,image/png,image/webp,image/heic';

interface Props {
  studentId: string;
  studentName: string;
  onClose: () => void;
}

/**
 * Supabase reports failures two ways — a `StorageError` is a real `Error`, a PostgREST
 * failure is a plain object carrying `message`. Testing only for `Error` would collapse every
 * database error here into a useless "Something went wrong".
 */
function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const message = (e as { message: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return 'Something went wrong.';
}

function PickupRow({
  pickup,
  studentName,
  photoUrl,
  onEnlarge,
  onError,
}: {
  pickup: AuthorizedPickup;
  studentName: string;
  photoUrl: string | null;
  onEnlarge: (url: string) => void;
  onError: (message: string | null) => void;
}) {
  const setPhoto = useSetAuthorizedPickupPhoto();
  const removePickup = useRemoveAuthorizedPickup();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'photo' | 'remove' | null>(null);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    onError(null);
    setBusy('photo');
    try {
      await setPhoto.mutateAsync({
        id: pickup.id,
        studentId: pickup.studentId,
        file,
        previousPath: pickup.photoPath,
        name: pickup.name,
        relationship: pickup.relationship,
        studentName,
      });
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleClearPhoto() {
    onError(null);
    setBusy('photo');
    try {
      await setPhoto.mutateAsync({
        id: pickup.id,
        studentId: pickup.studentId,
        file: null,
        previousPath: pickup.photoPath,
        name: pickup.name,
        relationship: pickup.relationship,
        studentName,
      });
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  // Takes the whole row: the delete is a hard delete, so this is the last moment these
  // details exist anywhere, and the audit event needs them.
  async function handleRemovePerson() {
    onError(null);
    setBusy('remove');
    try {
      await removePickup.mutateAsync({
        id: pickup.id,
        studentId: pickup.studentId,
        studentName,
        name: pickup.name,
        relationship: pickup.relationship,
        phone: pickup.phone,
        photoPath: pickup.photoPath,
        actorType: 'admin',
      });
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  const photoBusy = busy === 'photo';

  return (
    <li className="flex items-center gap-3 py-3">
      <StudentAvatar
        name={pickup.name}
        photoUrl={photoUrl}
        size="lg"
        onEnlarge={photoUrl ? () => onEnlarge(photoUrl) : undefined}
      />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{pickup.name}</p>
        {(pickup.relationship || pickup.phone) && (
          <p className="text-xs text-gray-500 truncate">
            {[pickup.relationship, pickup.phone].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-3">
          <button
            onClick={() => fileInput.current?.click()}
            disabled={photoBusy}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {photoBusy
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Camera className="h-3.5 w-3.5" />}
            {pickup.photoPath ? 'Replace photo' : 'Add photo'}
          </button>
          {pickup.photoPath && (
            <button
              onClick={() => void handleClearPhoto()}
              disabled={photoBusy}
              className="text-xs text-gray-400 hover:text-red-600 disabled:opacity-50"
            >
              Remove photo
            </button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            // Cleared before the await so choosing the same file twice still fires a change.
            e.target.value = '';
            void handleFile(file);
          }}
        />
      </div>

      <button
        onClick={() => void handleRemovePerson()}
        disabled={busy !== null}
        aria-label={`Remove ${pickup.name}`}
        className="p-1.5 text-gray-300 hover:text-red-600 rounded-md hover:bg-gray-50 disabled:opacity-50"
      >
        {busy === 'remove'
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : <Trash2 className="h-4 w-4" />}
      </button>
    </li>
  );
}

export function PickupContactsModal({ studentId, studentName, onClose }: Props) {
  const { data: pickups = [], isLoading } = useAuthorizedPickups(studentId);
  const addPickup = useAddAuthorizedPickup();

  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [phone, setPhone] = useState('');
  const [enlarged, setEnlarged] = useState<{ name: string; url: string } | null>(null);

  const { data: photoUrls = {} } = useStudentPhotoUrls(pickups.map(p => p.photoPath));

  async function handleAdd() {
    if (!name.trim()) { setError('Name is required.'); return; }
    setError(null);
    try {
      await addPickup.mutateAsync({
        studentId,
        studentName,
        name,
        relationship,
        phone,
        actorType: 'admin',
      });
      setName(''); setRelationship(''); setPhone(''); setShowForm(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <>
      <Modal isOpen onClose={onClose} title={`Pickup contacts — ${studentName}`}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Adults this family has authorized to collect {studentName}. Photos appear on the
            teacher's check-out sheet beside the name, so whoever is at the door can be
            confirmed by more than a name. They are stored privately and shown only to staff
            who already have this child on a roster.
          </p>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2].map(i => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg" />)}
            </div>
          ) : pickups.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {pickups.map(p => (
                <PickupRow
                  key={p.id}
                  pickup={p}
                  studentName={studentName}
                  photoUrl={p.photoPath ? photoUrls[p.photoPath] ?? null : null}
                  onEnlarge={url => setEnlarged({ name: p.name, url })}
                  onError={setError}
                />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">
              No authorized pickups listed. Parents and guardians linked to this student can
              always collect them — this list is for everyone else.
            </p>
          )}

          {showForm ? (
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <input
                type="text"
                placeholder="Name *"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Relationship"
                  value={relationship}
                  onChange={e => setRelationship(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="text-xs text-gray-400">
                Add the person first, then attach their photo from the row above.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => void handleAdd()}
                  disabled={addPickup.isPending}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {addPickup.isPending ? 'Adding…' : 'Add person'}
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setName(''); setRelationship(''); setPhone(''); setError(null);
                  }}
                  aria-label="Cancel adding a person"
                  className="px-3 py-2 border border-gray-200 text-gray-500 text-sm rounded-lg hover:bg-gray-50"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              <Plus className="h-4 w-4" /> Add person
            </button>
          )}
        </div>
      </Modal>

      {enlarged && (
        <StudentPhotoLightbox
          name={enlarged.name}
          photoUrl={enlarged.url}
          subtitle={`Authorized to collect ${studentName}`}
          onClose={() => setEnlarged(null)}
        />
      )}
    </>
  );
}
